import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

export type PendingImportStatus =
  | "PENDING"
  | "CONFIRMED"
  | "REJECTED"
  | "EXPIRED"
  | "FAILED";

export interface PendingCostImport {
  id: string; // UUID
  profileId: string; // UUID
  vendorConnectionProfileId?: number | null;
  attachmentId?: number | null;
  status: PendingImportStatus;
  emailSubject?: string;
  emailSenderAddress?: string;
  fileName?: string;
  detectedSystemId?: string; // the vendor systemId detected from email
  receivedAt: string; // ISO datetime
  processedAt?: string;
  processedBy?: string;
  totalRows?: number;
  validRows?: number;
  errorRows?: number;
  errorMessage?: string;
  overrideExisting: boolean;
  rateCount: number;
  createdBy?: string;
  updatedBy?: string;
  createdDate: string;
  timestamp?: string;
  version: number;
}

export interface PendingCostImportRow {
  id: string; // UUID
  pendingImportId: string; // UUID
  operatorId?: number;
  cost?: number; // decimal
  effectiveDate?: string;
  rawMcc?: string;
  rawMnc?: string;
  rawMccmnc?: string;
  rawCountry?: string;
  rawOperator?: string;
  rowNumber: number;
  hasError: boolean;
  errorMessage?: string;
  createdDate: string;
}

export interface PendingCostImportParams extends Params<PendingCostImport> {
  status?: PendingImportStatus;
  profileId?: string;
  emailSenderAddress?: string;
  fromDate?: Date;
  toDate?: Date;
}

export interface PendingCostImportRowParams
  extends Params<PendingCostImportRow> {}

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);
const BASE_PATH = "/pending-cost-import";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback;

export async function fetchPendingCostImports(
  params: PendingCostImportParams = {}
): Promise<IPageable<PendingCostImport>> {
  try {
    const response = await axiosInstance.get(BASE_PATH, { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching pending cost imports."
      )
    );
  }
}

export async function fetchPendingCostImportById(
  id: string
): Promise<PendingCostImport> {
  try {
    const response = await axiosInstance.get(`${BASE_PATH}/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching the pending cost import."
      )
    );
  }
}

export async function fetchPendingCostImportRows(
  pendingImportId: string,
  params: PendingCostImportRowParams = {},
  hasError?: boolean
): Promise<IPageable<PendingCostImportRow>> {
  try {
    const effectiveParams: Record<string, unknown> = { ...params };
    if (hasError !== undefined) {
      effectiveParams.hasError = hasError;
    }
    const response = await axiosInstance.get(
      `${BASE_PATH}/${pendingImportId}/rows`,
      { params: effectiveParams }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching pending cost import rows."
      )
    );
  }
}

export async function fetchPendingCostImportsByProfile(
  profileId: string
): Promise<PendingCostImport[]> {
  try {
    const response = await axiosInstance.get(
      `${BASE_PATH}/by-profile/${profileId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching pending cost imports for the profile."
      )
    );
  }
}

export async function confirmPendingCostImport(
  id: string,
  vendorConnectionProfileId?: string
): Promise<void> {
  try {
    const params = vendorConnectionProfileId
      ? { vendorConnectionProfileId }
      : undefined;
    await axiosInstance.post(`${BASE_PATH}/${id}/confirm`, {}, { params });
  } catch (error: any) {
    throw new Error(
      getErrorMessage(error, "An error occurred while confirming the import.")
    );
  }
}

export async function rejectPendingCostImport(id: string): Promise<void> {
  try {
    await axiosInstance.post(`${BASE_PATH}/${id}/reject`);
  } catch (error: any) {
    throw new Error(
      getErrorMessage(error, "An error occurred while rejecting the import.")
    );
  }
}

export async function deletePendingCostImport(id: string): Promise<void> {
  try {
    await axiosInstance.delete(`${BASE_PATH}/${id}`);
  } catch (error: any) {
    throw new Error(
      getErrorMessage(error, "An error occurred while deleting the import.")
    );
  }
}

export const usePendingCostImportsQuery = (
  params: PendingCostImportParams
): UseQueryResult<IPageable<PendingCostImport>, Error> => {
  const effectiveParams: PendingCostImportParams = {
    ...params,
    sort: params.sort || "receivedAt,desc",
  };
  const queryKey = ["pending-cost-import", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchPendingCostImports(effectiveParams),
  });
};

export const usePendingCostImportQuery = (
  id: string
): UseQueryResult<PendingCostImport, Error> => {
  const queryKey = ["pending-cost-import", id];
  return useQuery({
    queryKey,
    queryFn: () => fetchPendingCostImportById(id),
    enabled: Boolean(id),
  });
};

export interface VendorOption {
  id: string;
  name: string;
  systemId: string;
}

export async function fetchVendorsForProfile(
  profileId: string
): Promise<VendorOption[]> {
  try {
    const response = await axiosInstance.get("/connection-profiles/vendors", {
      params: { profileId, size: 1000 },
    });
    return response.data.content.map((vendor: any) => ({
      id: vendor.id,
      name: vendor.name,
      systemId: vendor.systemId,
    }));
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching vendors for the profile."
      )
    );
  }
}

export const useVendorsForProfileQuery = (
  profileId: string,
  queryEnabled: boolean = true
): UseQueryResult<VendorOption[], Error> => {
  return useQuery({
    queryKey: ["vendors-for-profile", profileId],
    queryFn: () => fetchVendorsForProfile(profileId),
    enabled: Boolean(profileId) && queryEnabled,
  });
};
